<div class="contacts-settings-wrapper">
  <div class="content-center">
    <!-- Loading State -->
    <div *ngIf="isLoading" class="loading-state">
      <div class="spinner"></div>
      <p>Loading contacts...</p>
    </div>

    <!-- Error State -->
    <div *ngIf="errorMessage && !isLoading" class="error-state">
      <div class="error-icon">⚠️</div>
      <p>{{ errorMessage }}</p>
      <button class="btn retry-btn" (click)="retry()">Retry</button>
    </div>

    <!-- Main Content -->
    <div *ngIf="!isLoading && !errorMessage">
      <!-- top action row -->
      <div class="tab-actions" [class.dimmed]="showNewInline">
        <a class="new-service-link" (click)="openNewContact()">+ Add Contact</a>
      </div>

      <!-- New/Edit Contact inline card -->
      <div
        class="new-contact-inline"
        *ngIf="showNewInline"
        role="dialog"
        aria-modal="true"
        [attr.aria-label]="isEditMode ? 'Edit Contact' : 'Add Contact'"
      >
        <div class="new-contact-header">
          <wm-common-text [data]="{ text: isEditMode ? 'Edit Contact' : 'Add Contact' }"></wm-common-text>
          <button class="modal-close" (click)="closeNewContact()" aria-label="Close">
            <mat-icon>close</mat-icon>
          </button>
        </div>

        <form class="modal-form" (ngSubmit)="saveNewContact()" [formGroup]="form">
          
          <div class="row">
            <div class="col">
              <wm-common-textfield
                [data]="{ label: 'First Name', placeholder: 'First Name' }"
                [(ngModel)]="newContact.firstName"
                formControlName="firstName"
                name="contact-first-name"
                required
              ></wm-common-textfield>
              <div *ngIf="form.get('firstName')?.invalid && form.get('firstName')?.touched" class="error-message">
                First name is required
              </div>
            </div>

            <div class="col">
              <wm-common-textfield
                [data]="{ label: 'Last Name', placeholder: 'Last Name' }"
                [(ngModel)]="newContact.lastName"
                formControlName="lastName"
                name="contact-last-name"
              ></wm-common-textfield>
            </div>
          </div>

          <div class="row">
            <div class="col">
              <wm-common-textfield
                [data]="{ label: 'Phone Number', placeholder: 'Phone Number' }"
                [(ngModel)]="newContact.phone"
                formControlName="phone"
                name="contact-phone"
              ></wm-common-textfield>
              <div *ngIf="form.get('phone')?.invalid && form.get('phone')?.touched" class="error-message">
                Please enter a valid phone number (e.g., 123-456-7890)
              </div>
            </div>

            <div class="col">
              <wm-common-textfield
                [data]="{ label: 'Email Address', placeholder: 'Email Address' }"
                [(ngModel)]="newContact.email"
                formControlName="email"
                name="contact-email"
              ></wm-common-textfield>
              <div *ngIf="form.get('email')?.invalid && form.get('email')?.touched" class="error-message">
                Please enter a valid email address
              </div>
            </div>
          </div>

          <div class="row">
            <div class="col">
              <wm-common-textfield
                [data]="{ label: 'Title', placeholder: 'Title' }"
                [(ngModel)]="newContact.title"
                formControlName="title"
                name="contact-title"
              ></wm-common-textfield>
            </div>

            <div class="col">
              <wm-common-textfield
                [data]="{ label: 'Office Hours', placeholder: 'Office Hours' }"
                [(ngModel)]="newContact.officeHours"
                formControlName="officeHours"
                name="contact-office-hours"
              ></wm-common-textfield>
            </div>
          </div>

          <div class="row">
            <div class="col">
              <wm-common-textfield
                [data]="{ label: 'Street Address', placeholder: 'Street Address' }"
                [(ngModel)]="newContact.streetAddress"
                formControlName="streetAddress"
                name="contact-street-address"
              ></wm-common-textfield>
            </div>

            <div class="col">
              <wm-common-textfield
                [data]="{ label: 'Address 2', placeholder: 'Address 2' }"
                [(ngModel)]="newContact.address2"
                formControlName="address2"
                name="contact-address-2"
              ></wm-common-textfield>
            </div>
          </div>

          <div class="row">
            <div class="col">
              <wm-common-textfield
                [data]="{ label: 'City', placeholder: 'City' }"
                [(ngModel)]="newContact.city"
                formControlName="city"
                name="contact-city"
              ></wm-common-textfield>
            </div>

            <div class="col">
              <wm-common-textfield
                [data]="{ label: 'State', placeholder: 'State' }"
                [(ngModel)]="newContact.state"
                formControlName="state"
                name="contact-state"
              ></wm-common-textfield>
            </div>
          </div>

          <div class="row">
            <div class="col">
              <wm-common-textfield
                [data]="{ label: 'Zip', placeholder: 'Zip' }"
                [(ngModel)]="newContact.zip"
                formControlName="zip"
                name="contact-zip"
              ></wm-common-textfield>
            </div>
            
            <div class="col"></div>
          </div>

          <div class="form-actions modal-actions">
            <button type="button" class="btn cancel" (click)="closeNewContact()">CANCEL</button>
            <button type="submit" class="btn save" [disabled]="!form.valid || isSaving">
              <span *ngIf="isSaving">SAVING...</span>
              <span *ngIf="!isSaving">SAVE</span>
            </button>
          </div>
        </form>
      </div>

      <!-- contacts table (dimmed when adding/editing) -->
      <div class="contacts-list-container" [class.dimmed]="showNewInline">
        <!-- Empty State -->
        <div *ngIf="tableData.length === 0" class="empty-state">
          <p>No contacts found. Add your first contact!</p>
        </div>

        <!-- Contacts Table -->
        <div *ngIf="tableData.length > 0" class="table-container">
          <table class="sticky-table">
            <thead>
              <tr>
                <th *ngFor="let head of tableHeaders">{{ head }}</th>
              </tr>
            </thead>

            <tbody>
              <tr *ngFor="let row of tableData; let i = index">
                <td class="col-name">{{ row.name }}</td>
                <td class="col-phone">{{ row.phone }}</td>
                <td class="col-email">{{ row.email }}</td>

                <!-- Actions column -->
                <td class="actions">
                  <button
                    class="action-btn edit"
                    aria-label="Edit {{ row.name }}"
                    (click)="editContact(i)"
                    title="Edit"
                  >
                    Edit
                  </button>
                  <button
                    class="action-btn delete"
                    aria-label="Delete {{ row.name }}"
                    (click)="requestDelete(i)"
                    title="Delete"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>

  <!-- Confirmation Modal -->
  <wm-common-confirmation-modal
    [data]="confirmData"
    (onYesClick)="confirmDelete()"
    (onNoClick)="cancelDelete()"
    [isApplicantMobileview]="true"
  ></wm-common-confirmation-modal>
</div>